import React from 'react';
import { useNavigate } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { Link, Tooltip, Typography } from '@mui/material';
import {
    formatBool,
    formatDate,
    formatDateTime,
    formatNumber,
    formatSubscriptionDate
} from '../../utils/parsing';

import { COLOR_LIST_BACKGROUND } from '../../theme';

export type RowTypeType =
    'email'
    | 'phone'
    | 'website'
    | 'bool'
    | 'y/n'
    | 'date'
    | 'datetime'
    | 'currency'
    | 'km'
    | 'kg'
    | 'number'
    | 'raw'
    | 'array'
    | 'subscription';

type RowType = {
    name: string,
    value: string | number | boolean | JSX.Element | string[] | object | null,
    valuePadding?: number,
    type?: RowTypeType,
    url?: string,
    isHidden?: boolean,
    infoTooltip?: { icon: JSX.Element, title: string }
}

type SubscriptionType = {
    endDate: string | null,
    isActive: boolean,
    isCarrier: boolean
};

type PropsType = {
    data: RowType[],
    size?: 'small' | 'medium'
};

function getValue(row: RowType) {
    const subscriptionValue = row.value as SubscriptionType;

    switch (row.type) {
        case 'email':
            return <Link href={`mailto:${row.value}`}>{row.value}</Link>;

        case 'phone':
            return <Link href={`tel:${row.value}`}>{row.value}</Link>;

        case 'website':
            return (
                <Link
                    href={
                        (row.value !== null && typeof row.value === 'string' && row.value.startsWith('www.'))
                            ? `https://${row.value}`
                            : `${row.value}`
                    }
                    target="_blank"
                >
                    {typeof row.value === 'string' ? row.value.replace(/^https?:\/\//, '') : row.value}
                </Link>
            );
        case 'bool':
            return formatBool(row.value);

        case 'y/n':
            return row.value ? 'Ja' : 'Nej';

        case 'date':
            return formatDate(row.value);

        case 'datetime':
            return formatDateTime(row.value);

        case 'currency':
            return formatNumber(row.value, 'SEK');

            // case 'pieces':
            //     return formatNumber(row.value, 'st');

        case 'km':
            return formatNumber(row.value, 'km');

        case 'kg':
            return formatNumber(row.value, 'kg');

        case 'number':
            return formatNumber(row.value, 'st');

        case 'array':
            return Array.isArray(row.value) ? row.value.join('\n') : '';

        case 'subscription':
            return formatSubscriptionDate({
                endDate: subscriptionValue.endDate,
                isActive: subscriptionValue.isActive,
                isCarrier: subscriptionValue.isCarrier
            });

        default:
            return row.value;
    }
}

const BasicList: React.FC<PropsType> = (props) => {
    const { data, size } = props;
    const navigate = useNavigate();

    return (
        <TableContainer>
            <Table size={size} aria-label="basic list">
                <TableBody>
                    {data.map((row) => !row.isHidden && (
                        <TableRow
                            key={row.name}
                            sx={{
                                '&:last-child td, &:last-child th': { border: 0 },
                                ':hover': {
                                    cursor: row.url ? 'pointer' : 'inherit',
                                    background: COLOR_LIST_BACKGROUND
                                }
                            }}
                            onClick={
                                row.url
                                    ? () => navigate(row.url as string)
                                    : undefined
                            }
                        >
                            <TableCell sx={{ verticalAlign: row.type === 'raw' ? 'middle' : 'top' }}>
                                <Typography
                                    variant="subtitle2"
                                    component="span"
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                >
                                    {row.name}
                                    {row.infoTooltip && (
                                        <Tooltip title={row.infoTooltip.title}>
                                            {row.infoTooltip.icon}
                                        </Tooltip>
                                    )}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{
                                verticalAlign: row.type === 'raw' ? 'middle' : 'top',
                                ...(row.valuePadding !== undefined ? { py: row.valuePadding } : {})
                            }}>
                                {row.type === 'raw' ? row.value : (
                                    <Typography
                                        variant="body2"
                                        sx={
                                            row.type === 'array'
                                                ? { whiteSpace: 'pre-line' }
                                                : undefined
                                        }
                                    >
                                        {getValue(row)}
                                    </Typography>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default BasicList;
